// OpenAI API 服务
// 用于智能评语生成与分析

import OpenAI from 'openai';

// 初始化 OpenAI 客户端
// 注意: API Key 应存储在环境变量或 Firebase Functions 中
const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // 生产环境应使用后端调用
});

/**
 * 优化教练评语
 * @param {string} rawFeedback - 教练原始评语 (中文/英文)
 * @param {string} studentLevel - 学员水平 (beginner/intermediate/advanced)
 * @returns {Promise<Object>} { original, optimized, translation }
 */
export const optimizeFeedback = async (rawFeedback, studentLevel = 'intermediate') => {
  try {
    const prompt = `
你是一位专业的驾驶教练助手。请帮助优化以下教学评语:

原始评语: "${rawFeedback}"
学员水平: ${studentLevel}

要求:
1. 保持专业、鼓励的语气
2. 突出具体的技能点和改进方向
3. 使用标准的驾驶术语
4. 长度控制在 100-150 词
5. 如果原文是中文，先翻译成英文，再优化
6. 返回 JSON 格式: { original, optimized, keyPoints, suggestions }

示例:
{
  "original": "今天表现不错，转弯还需要练习",
  "optimized": "Today's lesson showed great progress! Your parallel parking has improved significantly. Focus areas for next session: 1) Check blind spots more frequently during lane changes, 2) Practice smoother braking at intersections. Keep up the good work!",
  "keyPoints": ["Parallel parking improved", "Needs blind spot awareness"],
  "suggestions": ["More frequent mirror checks", "Practice controlled braking"]
}
`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4-turbo-preview',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.7,
      response_format: { type: 'json_object' }
    });

    return JSON.parse(response.choices[0].message.content);
  } catch (error) {
    console.error('优化评语失败:', error);
    // 失败时返回原文
    return {
      original: rawFeedback,
      optimized: rawFeedback,
      keyPoints: [],
      suggestions: []
    };
  }
};

/**
 * 从大量教学日志中提取常用评语模板
 * @param {Array<string>} logEntries - 历史评语列表
 * @returns {Promise<Array>} 常用模板数组
 */
export const extractCommonPhrases = async (logEntries) => {
  try {
    const prompt = `
分析以下驾驶教学评语，提取出最常用的 20 个评价短语:

评语列表:
${logEntries.join('\n---\n')}

要求:
1. 分类: 优点表扬、需改进项、安全提醒、技能建议
2. 提供缩写代码 (2-4 个字母)
3. 返回 JSON 数组

示例:
[
  {
    "code": "PK+",
    "category": "优点",
    "phrase": "Parallel parking executed perfectly with proper spacing",
    "chinese": "平行停车完成得很好"
  },
  {
    "code": "BS",
    "category": "改进",
    "phrase": "Remember to check blind spots before lane changes",
    "chinese": "变道前记得检查盲点"
  }
]
`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4-turbo-preview',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.5,
      response_format: { type: 'json_object' }
    });

    return JSON.parse(response.choices[0].message.content);
  } catch (error) {
    console.error('提取常用短语失败:', error);
    return [];
  }
};

/**
 * 分析学员进度趋势
 * @param {Array<Object>} progressData - 学员历史数据
 * @returns {Promise<Object>} 分析报告
 */
export const analyzeProgress = async (progressData) => {
  try {
    const prompt = `
分析学员的学习进度数据，生成趋势报告:

数据:
${JSON.stringify(progressData, null, 2)}

返回 JSON:
{
  "overallTrend": "improving/stable/declining",
  "strengths": ["技能1", "技能2"],
  "weaknesses": ["需改进1", "需改进2"],
  "recommendation": "下一步学习建议",
  "estimatedCompletion": "预计完成时间"
}
`;

    const response = await openai.chat.completions.create({
      model: 'gpt-4-turbo-preview',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.6,
      response_format: { type: 'json_object' }
    });

    return JSON.parse(response.choices[0].message.content);
  } catch (error) {
    console.error('分析进度失败:', error);
    return null;
  }
};

/**
 * 预设评语模板（离线使用）
 */
export const PRESET_TEMPLATES = {
  // 优点表扬
  praise: [
    { code: 'EX', text: 'Excellent performance today!', cn: '今天表现优秀！' },
    { code: 'IP', text: 'Significant improvement shown.', cn: '进步明显' },
    { code: 'SF', text: 'Very safety-conscious driving.', cn: '安全意识很强' }
  ],
  // 需改进
  improve: [
    { code: 'BS', text: 'Check blind spots more frequently.', cn: '多检查盲点' },
    { code: 'SC', text: 'Work on speed control.', cn: '控制好速度' },
    { code: 'ST', text: 'Smoother steering needed.', cn: '方向盘更平稳' }
  ],
  // 技能项
  skills: [
    { code: 'PK', text: 'Parking', cn: '停车' },
    { code: 'PP', text: 'Parallel Parking', cn: '平行停车' },
    { code: 'LC', text: 'Lane Change', cn: '变道' },
    { code: 'TN', text: 'Turning', cn: '转弯' },
    { code: 'BK', text: 'Backing', cn: '倒车' }
  ]
};
